import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '../ui/badge';
import { Package, ChevronRight, User } from 'lucide-react';

const ORDER_STATUS_CONFIG = {
  new: { label: 'New', className: 'bg-blue-100 text-blue-700' },
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700' },
  payment_confirmed: { label: 'Paid', className: 'bg-emerald-100 text-emerald-700' },
  processing: { label: 'Processing', className: 'bg-violet-100 text-violet-700' },
  ready_to_ship: { label: 'Ready', className: 'bg-indigo-100 text-indigo-700' },
  shipped: { label: 'Shipped', className: 'bg-blue-100 text-blue-700' },
  out_for_delivery: { label: 'Out for Delivery', className: 'bg-cyan-100 text-cyan-700' },
  delivered: { label: 'Delivered', className: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700' },
  return_requested: { label: 'Return', className: 'bg-orange-100 text-orange-700' },
  refunded: { label: 'Refunded', className: 'bg-gray-100 text-gray-600' }
};

const OrdersListLite = ({ orders = [], onView, loading = false }) => {
  const navigate = useNavigate();

  const getTimeAgo = (date) => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: false });
    } catch {
      return '';
    }
  };

  const getStatusConfig = (status) => {
    return ORDER_STATUS_CONFIG[status?.toLowerCase()] || { label: status || 'Unknown', className: 'bg-gray-100 text-gray-600' };
  };

  const getItemCount = (order) => {
    if (order.items?.length) return order.items.length;
    if (order.item_count) return order.item_count;
    return 1;
  };

  const getCustomerName = (order) => {
    return order.customer?.name || order.customer_name || order.shipping_address?.name || 'Customer';
  };

  const getOrderTotal = (order) => {
    // Try direct fields first
    const direct = order.total_amount || order.total || order.grand_total
      || order.amounts?.total || order.amounts?.grand_total
      || order.pricing?.total;
    if (direct && parseFloat(direct) > 0) return parseFloat(direct);
    // Fallback: compute from items
    const items = order.items || order.line_items || [];
    if (items.length > 0) {
      const itemsSum = items.reduce((sum, it) => {
        const qty = parseInt(it.quantity || it.qty || 1);
        const price = parseFloat(it.price || it.unit_price || 0);
        return sum + (qty * price);
      }, 0);
      const shipping = parseFloat(order.amounts?.shipping || order.pricing?.shipping || 0);
      const tax = parseFloat(order.amounts?.tax || order.pricing?.tax || 0);
      return itemsSum + shipping + tax;
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="space-y-3 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i.id || i.name || `${i.title || "i"}-${i}`} className="bg-white p-4 rounded-xl animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3 mb-3"></div>
            <div className="h-5 bg-gray-200 rounded w-1/4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center bg-white">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Package className="h-8 w-8 text-gray-400" />
        </div>
        <p className="text-gray-900 font-medium text-base mb-1">No orders yet</p>
        <p className="text-gray-500 text-sm">Orders will appear here when customers place them</p>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="orders-list-lite">
      {orders.map((order, index) => {
        const statusConfig = getStatusConfig(order.status);

        return (
          <div 
            key={order.id || order._id}
            className={`p-4 active:bg-gray-50 transition-colors cursor-pointer ${
              index !== orders.length - 1 ? 'border-b border-gray-100' : ''
            }`}
            onClick={() => navigate(`/orders/${order.id || order._id}`)}
            data-testid={`order-item-${order.id || order._id}`}
          >
            {/* Top Row: Order ID, Time, Status */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">
                  #{order.order_number || order.id?.slice(-6) || 'N/A'}
                </span>
                <span className="text-xs text-gray-400">
                  {getTimeAgo(order.created_at)}
                </span>
              </div>
              <Badge className={`text-[10px] px-2 py-0.5 font-medium rounded-full ${statusConfig.className}`}>
                {statusConfig.label}
              </Badge>
            </div>
            
            {/* Middle: Items & Customer */}
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
              <span className="font-medium text-gray-600">{getItemCount(order)} item{getItemCount(order) > 1 ? 's' : ''}</span>
              <span className="text-gray-300">•</span>
              <User className="h-3 w-3 text-gray-400" />
              <span className="truncate max-w-[160px]">{getCustomerName(order)}</span>
            </div>
            
            {/* Bottom: Price */}
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-gray-900">
                ₹{getOrderTotal(order).toLocaleString('en-IN')}
              </span>
              <ChevronRight className="h-5 w-5 text-gray-300" />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OrdersListLite;
